import {
  DataGrid as FluentUIDataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  DataGridBody,
  DataGridCell,
  createTableColumn,
  TableColumnDefinition,
} from "@fluentui/react-components";
import { useMemo } from "react";
import Typography from "../Typography";
import { customTokens } from "../../theme/theme";
import { useDataGridStyles } from "./DataGrid.styles";
import { ArrowSort24Regular } from "@fluentui/react-icons";

export type ColumnConfig = {
  key: string;
  label: string;
  numeric?: boolean;
};

export type SortState = {
  sortColumn: string;
  sortDirection: "ascending" | "descending";
};

export type DataGridProps = {
  items: any[];
  columnConfig: ColumnConfig[];
  selectionMode?: "single" | "multiselect";
  sortable?: boolean;
  resizableColumns?: boolean;
  sortState?: any;
  onSortChange?: any;
};

export const DataGrid = ({
  items,
  columnConfig,
  selectionMode,
  sortable = true,
  resizableColumns = true,
  sortState,
  onSortChange,
}: DataGridProps) => {
  const styles = useDataGridStyles();

  const columns = useMemo<TableColumnDefinition<any>[]>(
    () =>
      columnConfig.map((col) =>
        createTableColumn({
          columnId: col.key,
          compare: (a: any, b: any) =>
            col.numeric
              ? Number(a[col.key]) - Number(b[col.key])
              : String(a[col.key] ?? "").localeCompare(
                  String(b[col.key] ?? "")
                ),
          renderHeaderCell: () => (
            <Typography
              variant="body2"
              mobileVariant="body1"
              color={customTokens.colorNeutralGrey94}
            >
              {col.label}
            </Typography>
          ),
          renderCell: (item: any) => {
            const value = item[col.key];

            // Check if it's a number and format it
            const formattedValue =
              typeof value === "number"
                ? value.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : String(value ?? "");

            return (
              <Typography variant="body2" mobileVariant="body1">
                {formattedValue}
              </Typography>
            );
          },
        })
      ),
    [columnConfig]
  );

  return (
    <div className={styles.dataGridContainer}>
      <FluentUIDataGrid
        items={items}
        columns={columns}
        sortable={sortable}
        resizableColumns={resizableColumns}
        focusMode="composite"
        selectionMode={selectionMode}
        sortState={sortState}
        onSortChange={onSortChange}
        className={styles.dataGrid}
        resizableColumnsOptions={{ autoFitColumns: false }}
      >
        <DataGridHeader>
          <DataGridRow>
            {({ renderHeaderCell }) => (
              <DataGridHeaderCell sortIcon={<ArrowSort24Regular />}>
                {renderHeaderCell()}
              </DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>

        <DataGridBody>
          {({ item, rowId }) => (
            <DataGridRow key={rowId}>
              {({ renderCell }) => (
                <DataGridCell>{renderCell(item)}</DataGridCell>
              )}
            </DataGridRow>
          )}
        </DataGridBody>
      </FluentUIDataGrid>
    </div>
  );
};
